import React, { useContext, useState } from "react";
import {
  ExclamationCircleIcon, ExclamationTriangleIcon,
  PlusSmallIcon,
} from "@heroicons/react/24/outline";
import { useHistory } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import CountryContext from "../CountryContext";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "../ui-components/Button";
import _ from "lodash";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import NotificationsCount from "../ui-components/NotificationsCount";
import WorksiteReferenceReportModal from "components/Modals/WorksiteReferenceReportModal";
import NotificationPing from "../ui-components/NotificationPing";
import { customSort } from "utils/constants";

const WorksiteDetailButtonGroup = ({
  selectedWorksite,
  userData,
  references,
  newReferencesNotifications,
  scopes,
  freezed,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [sendingReferenceReport, setSendingReferenceReport] = useState(false);
  const [isReferenceReportModalOpen, setIsReferenceReportModalOpen] =
    useState(false);
  const notSortedSteps = Object.values(
    selectedWorksite.cod_worksite_status
  ).map((curr, idx, arr) => {
    const [prev, next] = [arr.slice(0, idx), arr.slice(idx, -1)];
    const isActive = curr.flg_active || next.some((s) => s.flg_active);
    const isCurrent = !isActive && prev.pop()?.flg_active;
    return { ...curr, flg_active: isActive, flg_current: isCurrent };
  });
  const steps = notSortedSteps.sort(customSort);
  const isCurrentStep = (stepName) =>
    steps.find(
      (step, idx) =>
        step.step === stepName &&
        (idx === steps.length - 1 || !steps[idx + 1].flg_active)
    )?.flg_active ?? false;
  const {
    country: { code },
  } = useContext(CountryContext);
  const isReferenceVisible =
    isCurrentStep("closed") && scopes.includes("reference:read");
  const buildReferenceButtonSubitems = () => {
    // add existing references (if any)
    const subitems =
      references?.map((reference) => ({
        title: (
          <span className="flex items-center font-semibold">
            {newReferencesNotifications?.some(
              (n) => n.cod_reference === reference.id
            ) ? (
              <NotificationPing className="mx-1" />
            ) : (
              <span
                className={`mx-1 ${
                  reference.closed
                    ? `text-${userData.business}-700`
                    : "text-yellow-300"
                }`}
              >
                &#x25cf;
              </span>
            )}
            <span className="mx-2">
              {reference.title ?? intl.formatMessage({ id: "untitled" })}
            </span>
          </span>
        ),
        onClick: () => {
          history.push(
            `/${code}/worksites/${selectedWorksite.id}/reference/${reference.id}`
          );
        },
      })) ?? [];
    // if user can create references, add create button
    if (scopes.includes("reference:create"))
      subitems.push({
        title: (
          <span className="flex items-center">
            <PlusSmallIcon className="w-4 h-4" />
            <span className="mx-2">
              {intl.formatMessage({ id: "create_new_reference" })}
            </span>
          </span>
        ),
        onClick: () => {
          history.push(`/${code}/worksites/${selectedWorksite.id}/reference`);
        },
      });
    // if there are no references and user cannot create new ones, add a 'no references' placeholder
    if (subitems.length === 0)
      subitems.push({
        title: (
          <span className="text-center italic mx-2 my-1">
            {intl.formatMessage({ id: "no_references_available" })}
          </span>
        ),
      });

    return subitems;
  };
  const dispatchReportReference = async (fieldId) => {
    setIsReferenceReportModalOpen(false);
    setSendingReferenceReport(true);

    try {
      const result = await countryApiRequest.put(
        `/worksites/${selectedWorksite.id}/report_reference`,
        {
          field_id: fieldId,
        }
      );
      setSelectedWorksite(result);
      push({
        type: "success",
        title: intl.formatMessage({ id: "reference_reported_successfully" }),
      });
    } catch (err) {
      push({
        type: "error",
        title: intl.formatMessage({ id: "an_error_occurred" }),
      });
      throw err;
    } finally {
      setSendingReferenceReport(false);
    }
  };
  return (
    <>
      {/* ----- Reference report field selection modal ----- */}
      <WorksiteReferenceReportModal
        isOpen={isReferenceReportModalOpen}
        onDismiss={() => {
          setIsReferenceReportModalOpen(false);
        }}
        onConfirm={dispatchReportReference}
        description={intl.formatMessage({
          id: "select_field_reference_report",
        })}
        showFieldsSelector={userData.flg_internal}
      />
      <div>
        {code === "it" && (
          <div className="flex mb-6">
            <div className="w-1/2 pr-4">
              <Button
                disabled={!isReferenceVisible || freezed}
                subitems={buildReferenceButtonSubitems()}
                subitemsDropdownCls="right-0"
                big
              >
                <span>{intl.formatMessage({ id: "references" })}</span>
                <NotificationsCount
                  count={newReferencesNotifications?.length}
                  marginLeftCls="ml-0.5"
                />
              </Button>
            </div>
            <div className="w-1/2">
              <Button
                styleType={
                  !!selectedWorksite.cod_report_field
                    ? ` bg-${userData.business}-400`
                    : ` bg-${userData.business}-700`
                }
                disabled={
                  freezed ||
                  isCurrentStep("planning") ||
                  isCurrentStep("deactivated") ||
                  sendingReferenceReport || // is sending
                  !!selectedWorksite.cod_report_field // has already been reported
                }
                onClick={() => {
                  if (!!selectedWorksite.cod_report_field) return;
                  setIsReferenceReportModalOpen(true);
                }}
                style={
                  !!selectedWorksite.cod_report_field ? { opacity: 1 } : {}
                }
                Icon={
                  !!selectedWorksite.cod_report_field
                    ? CheckCircleIcon
                    : sendingReferenceReport
                      ? ({className, ...props}) => <ArrowPathIcon className={`animate-spin ${className}`} {...props} />
                      : ExclamationTriangleIcon
                }
                big
              >
                <FormattedMessage
                  id={
                    !!selectedWorksite.cod_report_field
                      ? "reference_reported"
                      : "report_reference"
                  }
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default WorksiteDetailButtonGroup;
